import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider,
} from "@mui/material";
import { Save as SaveIcon } from "@mui/icons-material";
import { useAppVersion, useUpdateAppVersion } from "../../hooks/useAppVersion";

const AppVersionSettings: React.FC = () => {
  const { data: versionConfig, isLoading, error } = useAppVersion();
  const updateMutation = useUpdateAppVersion();

  const [minimumSupportedVersion, setMinimumSupportedVersion] = useState("");
  const [latestVersion, setLatestVersion] = useState("");
  const [updateMessage, setUpdateMessage] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (versionConfig) {
      setMinimumSupportedVersion(versionConfig.minimumSupportedVersion);
      setLatestVersion(versionConfig.latestVersion);
      setUpdateMessage(versionConfig.updateMessage || "");
    }
  }, [versionConfig]);

  const validateVersion = (version: string): boolean => {
    return /^\d+\.\d+\.\d+$/.test(version);
  };

  const handleSave = async () => {
    setValidationError(null);

    if (!validateVersion(minimumSupportedVersion)) {
      setValidationError("最小サポートバージョンの形式が正しくありません（例: 1.2.3）");
      return;
    }

    if (!validateVersion(latestVersion)) {
      setValidationError("最新バージョンの形式が正しくありません（例: 1.2.3）");
      return;
    }

    try {
      await updateMutation.mutateAsync({
        minimumSupportedVersion,
        latestVersion,
        updateMessage: updateMessage.trim() || null,
      });
    } catch (err) {
      console.error("Error updating app version:", err);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">バージョン設定の取得に失敗しました</Alert>;
  }

  return (
    <Box>
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        sx={{ fontSize: { xs: "1.5rem", sm: "2.125rem" } }}
      >
        アプリ設定
      </Typography>

      <Card sx={{ maxWidth: 600 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            バージョン管理
          </Typography>

          <Divider sx={{ mb: 3 }} />

          {validationError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {validationError}
            </Alert>
          )}

          {updateMutation.isSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              バージョン設定を更新しました
            </Alert>
          )}

          {updateMutation.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              更新に失敗しました
            </Alert>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <TextField
              label="最小サポートバージョン"
              value={minimumSupportedVersion}
              onChange={(e) => setMinimumSupportedVersion(e.target.value)}
              placeholder="1.2.1"
              helperText="このバージョン未満のアプリは強制アップデートが必要になります"
              fullWidth
            />

            <TextField
              label="最新バージョン"
              value={latestVersion}
              onChange={(e) => setLatestVersion(e.target.value)}
              placeholder="1.2.1"
              helperText="このバージョン未満のアプリには任意アップデートを促します"
              fullWidth
            />

            <TextField
              label="アップデートメッセージ（任意）"
              value={updateMessage}
              onChange={(e) => setUpdateMessage(e.target.value)}
              placeholder="新機能が追加されました"
              helperText="任意アップデート時に表示するメッセージ（空の場合はデフォルトメッセージ）"
              multiline
              rows={2}
              fullWidth
            />

            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={updateMutation.isLoading}
              sx={{ alignSelf: "flex-start" }}
            >
              {updateMutation.isLoading ? "保存中..." : "保存"}
            </Button>
          </Box>

          {versionConfig?.updatedAt && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 3, display: "block" }}>
              最終更新: {new Date(versionConfig.updatedAt._seconds * 1000).toLocaleString("ja-JP")}
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default AppVersionSettings;
